import { useAuth } from "@/context/AuthContext";
import { FileText, Calculator, Users, ShieldCheck, ArrowRight, Receipt } from "lucide-react";

const features = [
  { icon: Calculator, title: "Spreadsheet quote builder", body: "Line items, live totals, discounts and GST computed as you type." },
  { icon: ShieldCheck, title: "Quote vs Estimate", body: "Label documents correctly — fixed and binding, or an approximation." },
  { icon: Receipt, title: "GST-inclusive pricing", body: "Show the consumer price with a visible GST line, ABN on file." },
  { icon: Users, title: "Clients & price catalog", body: "Job addresses, saved services and materials with default rates." },
];

export default function Landing() {
  const { login } = useAuth();
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="absolute inset-x-0 top-0 h-[560px] bg-[radial-gradient(60%_60%_at_15%_0%,rgba(37,99,235,0.28),transparent)]" />
      <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
        <header className="flex items-center justify-between py-7">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-blue-600 flex items-center justify-center font-black text-sm tracking-tighter">WVT</div>
            <span className="font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>QuoteFlow</span>
          </div>
          <button data-testid="header-login-btn" onClick={login}
            className="text-sm font-medium text-slate-300 hover:text-white transition-colors">
            Sign in
          </button>
        </header>

        <section className="pt-16 pb-24 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold mb-5">
            Victoria / Australia compliant
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
            style={{ fontFamily: "Manrope, sans-serif" }}>
            Quote the job on site.
            <span className="block text-slate-500">Win it before you leave.</span>
          </h1>
          <p className="text-base text-slate-400 mt-7 max-w-xl leading-relaxed">
            QuoteFlow builds professional, GST-correct quotes and estimates for trades and service
            businesses — with your ABN, your rates and your branding.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="get-started-btn" onClick={login}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 rounded-full px-7 py-3.5 text-sm font-semibold transition-colors active:scale-95">
              Continue with Google <ArrowRight size={16} />
            </button>
            <span className="text-xs text-slate-500">No credit card. Set your ABN and start quoting.</span>
          </div>
        </section>

        <section className="grid sm:grid-cols-2 gap-px bg-slate-800 rounded-lg overflow-hidden border border-slate-800 mb-24">
          {features.map((f) => (
            <div key={f.title} className="bg-slate-900 p-7 hover:bg-slate-900/60 transition-colors">
              <f.icon size={20} className="text-blue-400" />
              <h3 className="font-semibold mt-4 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </section>

        <footer className="border-t border-slate-800 py-8 flex items-center gap-2 text-xs text-slate-600">
          <FileText size={14} /> QuoteFlow by WVT — general guidance only, not legal or tax advice.
        </footer>
      </div>
    </div>
  );
}
